import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../components/Button';
import { fetchVersions, promoteVersion, updateVersionTags } from '../services/mapManagementService';
import { useError } from '../contexts/ErrorContext';
import './MapVersionHistoryPage.css';

const GCS_BUCKET = import.meta.env.VITE_RESORT_DB_BUCKET || 'projectblanche.appspot.com';

const formatBytes = (n) => (typeof n === 'number' && n > 0
  ? `${(n / 1024 / 1024).toFixed(1)} MB`
  : null);

const formatWhen = (iso) => {
  if (!iso) return 'unknown';
  const d = new Date(iso);
  return `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
};

const delta = (featureDelta) => {
  if (!featureDelta?.before || !featureDelta?.after) return null;
  const { before, after } = featureDelta;
  return ['runs', 'lifts', 'pois'].map(kind => {
    const d = (after[kind] ?? 0) - (before[kind] ?? 0);
    return {
      kind,
      before: before[kind] ?? 0,
      after: after[kind] ?? 0,
      diff: d,
    };
  });
};

const MapVersionHistoryPage = () => {
  const navigate = useNavigate();
  const { addError } = useError();

  const [versions, setVersions] = useState(null);
  const [latest, setLatest] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState('');
  const [editingTags, setEditingTags] = useState(null);
  const [tagDraft, setTagDraft] = useState({ iosAppVersion: '', androidAppVersion: '', mapboxTilesVersion: '' });
  const [tagBusy, setTagBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const data = await fetchVersions();
      setVersions(data.versions);
      setLatest(data.latest);
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Map Version History');
    }
  }, [addError]);

  useEffect(() => { load(); }, [load]);

  const shown = useMemo(() => {
    if (!versions) return [];
    const q = filter.trim().toLowerCase();
    if (!q) return versions;
    return versions.filter(v =>
      (v.message || '').toLowerCase().includes(q)
      || (v.skiAreaName || '').toLowerCase().includes(q)
      || (v.skiAreaId || '').toLowerCase().includes(q)
      || (v.publishedByEmail || '').toLowerCase().includes(q));
  }, [versions, filter]);

  // The artifact is ~87 MB, so it is fetched with gcloud rather than through the
  // browser: no signed-URL IAM to maintain, and nothing streaming through
  // Cloud Run. The UI's job is to hand over the exact command.
  const [copied, setCopied] = useState(null);
  const copyCommand = useCallback(async (version) => {
    const cmd = `gcloud storage cp gs://${GCS_BUCKET}/${version.gcsPath} .`;
    try {
      await navigator.clipboard.writeText(cmd);
      setCopied(version.versionId);
      setTimeout(() => setCopied(c => (c === version.versionId ? null : c)), 2000);
    } catch {
      setError(`Copy failed. Run: ${cmd}`);
    }
  }, []);

  const handlePromote = useCallback(async (versionId) => {
    setBusy(versionId);
    setError(null);
    try {
      await promoteVersion(versionId);
      await load();
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Map Version History');
    } finally {
      setBusy(null);
    }
  }, [load, addError]);

  const startEditTags = useCallback((version) => {
    setEditingTags(version.versionId);
    setTagDraft({
      iosAppVersion: version.iosAppVersion || '',
      androidAppVersion: version.androidAppVersion || '',
      mapboxTilesVersion: version.mapboxTilesVersion || '',
    });
  }, []);

  const cancelEditTags = useCallback(() => setEditingTags(null), []);

  const saveTags = useCallback(async (versionId) => {
    setTagBusy(true);
    setError(null);
    try {
      const tags = await updateVersionTags(versionId, tagDraft);
      setVersions(vs => vs.map(v => (v.versionId === versionId ? { ...v, ...tags } : v)));
      setEditingTags(null);
    } catch (err) {
      setError(err.message);
      addError(err.message, err.stack, 'Map Version History');
    } finally {
      setTagBusy(false);
    }
  }, [tagDraft, addError]);

  return (
    <div className="mvh-page">
      <div className="mvh-header">
        <button className="mvh-back" onClick={() => navigate('/map-management')}>
          &larr; Map Management
        </button>
        <h2>Map Version History</h2>
        <p className="mvh-sub">
          Every published version of the resort database, newest first. The live
          version is what the goggles and every Offboard tool read.
        </p>
      </div>

      <div className="mvh-controls">
        <input
          type="text"
          placeholder="Filter by resort, message or author…"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
        />
        {latest && (
          <span className="mvh-live-path">
            live: <code>gs://{GCS_BUCKET}/{latest.gcsPath}</code>
          </span>
        )}
      </div>

      {error && <div className="mvh-error">{error}</div>}

      {!versions && !error && <div className="mvh-loading">Loading…</div>}

      {versions && versions.length === 1 && versions[0].isSeed && (
        <p className="mvh-note">
          Nothing has been imported yet — the timeline starts at the base dataset
          below. The first published import becomes v1.
        </p>
      )}

      {shown.length > 0 && (
        <ol className="mvh-timeline">
          {shown.map(v => {
            const live = latest && latest.versionId === v.versionId;
            const d = delta(v.featureDelta);
            return (
              <li key={v.versionId}
                className={`mvh-item${live ? ' live' : ''}${v.isSeed ? ' seed' : ''}`}>
                <div className="mvh-marker" aria-hidden="true" />
                <div className="mvh-card">
                  <div className="mvh-card-head">
                    <span className="mvh-version">
                      {v.isSeed ? 'Base' : `v${v.versionNumber}`}
                    </span>
                    {live && <span className="mvh-live">live</span>}
                    {v.isSeed && <span className="mvh-seed-tag">seed</span>}
                    <span className="mvh-resort">
                      {v.skiAreaName || v.skiAreaId || '—'}
                    </span>
                    <span className="mvh-when">{formatWhen(v.publishedAt)}</span>
                  </div>

                  <p className="mvh-message">{v.message || <em>No message</em>}</p>

                  <div className="mvh-meta">
                    <span>{v.publishedByEmail || (v.isSeed ? 'mapping pipeline' : 'unknown author')}</span>
                    {formatBytes(v.sizeBytes) && <span>{formatBytes(v.sizeBytes)}</span>}
                    {v.supersedes && <span>supersedes {v.supersedes}</span>}
                  </div>

                  {d && (
                    <div className="mvh-delta">
                      {d.map(({ kind, before, after, diff }) => (
                        <span key={kind} className="mvh-delta-item">
                          <span className="mvh-delta-kind">{kind}</span>
                          {before} &rarr; {after}
                          {diff !== 0 && (
                            <span className={diff > 0 ? 'mvh-up' : 'mvh-down'}>
                              {diff > 0 ? `+${diff}` : diff}
                            </span>
                          )}
                        </span>
                      ))}
                    </div>
                  )}

                  {v.boundary?.updated && (
                    <div className="mvh-boundary">
                      Boundary re-derived{v.boundary.geom_type ? ` (${v.boundary.geom_type})` : ''}
                    </div>
                  )}

                  {!v.isSeed && (
                    editingTags === v.versionId ? (
                      <div className="mvh-tags-edit">
                        <input
                          type="text"
                          placeholder="iOS app version"
                          value={tagDraft.iosAppVersion}
                          onChange={(e) => setTagDraft(t => ({ ...t, iosAppVersion: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder="Android app version"
                          value={tagDraft.androidAppVersion}
                          onChange={(e) => setTagDraft(t => ({ ...t, androidAppVersion: e.target.value }))}
                        />
                        <input
                          type="text"
                          placeholder="Mapbox tiles version"
                          value={tagDraft.mapboxTilesVersion}
                          onChange={(e) => setTagDraft(t => ({ ...t, mapboxTilesVersion: e.target.value }))}
                        />
                        <div className="mvh-tags-edit-actions">
                          <Button variant="primary" size="small"
                            onClick={() => saveTags(v.versionId)} disabled={tagBusy}>
                            Save
                          </Button>
                          <Button variant="outline" size="small"
                            onClick={cancelEditTags} disabled={tagBusy}>
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="mvh-tags">
                        {v.iosAppVersion && <span className="mvh-tag">iOS {v.iosAppVersion}</span>}
                        {v.androidAppVersion && <span className="mvh-tag">Android {v.androidAppVersion}</span>}
                        {v.mapboxTilesVersion && (
                          <span className="mvh-tag">Tiles {v.mapboxTilesVersion}</span>
                        )}
                        <button className="mvh-tags-edit-btn" onClick={() => startEditTags(v)}>
                          {v.iosAppVersion || v.androidAppVersion || v.mapboxTilesVersion
                            ? 'edit tags' : 'add version tags'}
                        </button>
                      </div>
                    )
                  )}

                  <div className="mvh-path">
                    <code>gs://{GCS_BUCKET}/{v.gcsPath}</code>
                    <button className="mvh-copy" onClick={() => copyCommand(v)}>
                      {copied === v.versionId ? 'copied' : 'copy gcloud command'}
                    </button>
                  </div>

                  <div className="mvh-actions">
                    {!live && !v.isSeed && (
                      <Button variant="outline" size="small"
                        onClick={() => handlePromote(v.versionId)}
                        disabled={busy === v.versionId}>
                        Make live
                      </Button>
                    )}
                    <span className="mvh-id">{v.versionId}</span>
                  </div>
                </div>
              </li>
            );
          })}
        </ol>
      )}

      {versions && versions.length > 0 && shown.length === 0 && (
        <div className="mvh-empty">No versions match that filter.</div>
      )}
    </div>
  );
};

export default MapVersionHistoryPage;
